import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Wallet, Loader2, Search, CheckCircle2, AlertTriangle, Landmark,
  Download, FileText, FileSpreadsheet, Mail, MessageCircle, MoreVertical, Plus, X, Send, Banknote, Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DirhamIcon } from '../components/icons/DirhamIcon';
import { Money } from '../components/ui/Money';
import { toast } from '../hooks/use-toast';
import { downloadBlob } from '../utils/downloadBlob';

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const aedText = (v) => `AED ${Math.round(Number(v || 0)).toLocaleString('en-US')}`;
const aed = (v) => <Money value={v} />;
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const STATUS_META = {
  pending: { label: 'Unpaid', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  partial: { label: 'Partial', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  vault: { label: 'In Vault', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

// Group rank: Unpaid (pending + partial) on top, then Vault, then Paid.
const GROUP_RANK = { pending: 0, partial: 0, vault: 1, paid: 2 };

const KpiTile = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div className={`rounded-xl border p-4 ${color}`} data-testid={testid}>
    <div className="flex items-center gap-2"><Icon className="h-4 w-4" /><span className="text-xs font-medium">{label}</span></div>
    <p className="text-2xl font-bold mt-1">{value}</p>
    {sub ? <p className="text-[11px] opacity-70 mt-0.5">{sub}</p> : null}
  </div>
);

const EOSBLedger = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [partial, setPartial] = useState(null);
  const [partialAmt, setPartialAmt] = useState('');
  const [financeOpen, setFinanceOpen] = useState(false);
  const [financeEmails, setFinanceEmails] = useState([]);
  const [newFinanceEmail, setNewFinanceEmail] = useState('');
  const [sendingFinance, setSendingFinance] = useState(false);
  const [view, setView] = useState('settlements'); // 'settlements' | 'active'
  const [active, setActive] = useState(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [activeSearch, setActiveSearch] = useState('');

  const loadActive = async () => {
    setActiveLoading(true);
    try {
      const r = await axios.get(`${API}/api/eosb-payments/active-summary`);
      setActive(r.data);
    } catch { setActive({ rows: [], totals: {}, count: 0 }); }
    finally { setActiveLoading(false); }
  };
  useEffect(() => { if (view === 'active' && !active) loadActive(); }, [view]); // eslint-disable-line

  const activeRows = useMemo(() => {
    const all = (active?.rows || []).slice();
    const q = activeSearch.trim().toLowerCase();
    return q ? all.filter((r) => `${r.name} ${r.employee_code} ${r.department}`.toLowerCase().includes(q)) : all;
  }, [active, activeSearch]);
  const activeTotals = active?.totals || {};

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/eosb-payments/summary/dashboard`);
      setData(r.data);
    } catch { setData({ floats: {}, counts: {}, rows: [] }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const floats = data?.floats || {};
  const counts = data?.counts || {};
  const rows = useMemo(() => {
    const all = (data?.rows || []).slice();
    const q = search.trim().toLowerCase();
    const filtered = q
      ? all.filter((r) => `${r.name} ${r.employee_code} ${r.department}`.toLowerCase().includes(q))
      : all;
    return filtered.sort((a, b) => {
      const g = (GROUP_RANK[a.status] ?? 9) - (GROUP_RANK[b.status] ?? 9);
      if (g !== 0) return g;
      return (b.total_amount || 0) - (a.total_amount || 0);
    });
  }, [data, search]);

  // ── inline actions ──
  const setStatus = async (row, status, amount = null) => {
    setBusyId(row.employee_id);
    try {
      await axios.put(`${API}/api/eosb-payments/${row.employee_id}/set-status`, { status, amount });
      toast({ title: 'EOSB updated', description: `${row.name} → ${STATUS_META[status]?.label || status}` });
      await load();
    } catch (e) { toast({ title: 'Update failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  const moveToVault = async (row) => {
    if (!window.confirm(`Move ${row.name}'s UNCLAIMED EOSB (AED ${Math.round(row.balance).toLocaleString()}) to the Company Vault? It will be marked resolved and stop appearing as pending.`)) return;
    setBusyId(row.employee_id);
    try {
      await axios.post(`${API}/api/eosb-payments/${row.employee_id}/move-to-vault`);
      toast({ title: 'Moved to Company Vault', description: `${row.name}'s unclaimed EOSB is now held in the vault.` });
      await load();
    } catch (e) { toast({ title: 'Action failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  const releaseAndPay = async (row) => {
    const amt = row.vault_amount || row.total_amount;
    if (!window.confirm(`Release ${row.name}'s EOSB from the Company Vault and record it as PAID OUT (AED ${Number(amt).toLocaleString(undefined, { minimumFractionDigits: 2 })})?`)) return;
    setBusyId(row.employee_id);
    try {
      await axios.post(`${API}/api/staff-vault/release`, { employee_id: row.employee_id, released_to: 'employee', notes: 'Released from vault & paid out (EOSB ledger)' });
      await axios.put(`${API}/api/eosb-payments/${row.employee_id}/mark-paid?total_amount=${row.total_amount}`);
      toast({ title: 'Released & paid out', description: `${row.name}'s EOSB settled and removed from the vault.` });
      await load();
    } catch (e) { toast({ title: 'Action failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setBusyId(null); }
  };

  const submitPartial = () => {
    const amt = parseFloat(partialAmt);
    if (!amt || amt <= 0) { toast({ title: 'Enter a valid amount', variant: 'destructive' }); return; }
    const row = partial; setPartial(null); setStatus(row, 'partial', amt);
  };

  // ── export ──
  const exportReport = async (format) => {
    try {
      const r = await axios.get(`${API}/api/eosb-payments/report/download?status=all&format=${format}`, { responseType: 'blob' });
      const type = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      downloadBlob(new Blob([r.data], { type }), `EOSB_Ledger.${format === 'excel' ? 'xlsx' : 'pdf'}`, type);
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
  };

  const exportActive = async (format) => {
    try {
      const r = await axios.get(`${API}/api/eosb-payments/active-summary/download?format=${format}`, { responseType: 'blob' });
      const type = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf';
      downloadBlob(new Blob([r.data], { type }), `Active_Staff_EOSB.${format === 'excel' ? 'xlsx' : 'pdf'}`, type);
    } catch { toast({ title: 'Download failed', variant: 'destructive' }); }
  };

  // ── finance email ──
  const openFinance = async () => {
    setFinanceOpen(true);
    try {
      const r = await axios.get(`${API}/api/settings/eosb-finance`);
      setFinanceEmails(r.data?.current?.finance_emails || []);
    } catch { /* leave empty */ }
  };
  const addFinanceEmail = () => {
    const e = newFinanceEmail.trim();
    if (!EMAIL_RE.test(e)) { toast({ title: 'Enter a valid email', variant: 'destructive' }); return; }
    if (financeEmails.some((x) => x.toLowerCase() === e.toLowerCase())) { setNewFinanceEmail(''); return; }
    setFinanceEmails((p) => [...p, e]); setNewFinanceEmail('');
  };
  const removeFinanceEmail = (e) => setFinanceEmails((p) => p.filter((x) => x !== e));
  const sendFinance = async () => {
    if (financeEmails.length === 0) { toast({ title: 'Add at least one email', variant: 'destructive' }); return; }
    setSendingFinance(true);
    try {
      const r = await axios.post(`${API}/api/settings/eosb-finance/send-now`, { recipients: financeEmails, save: true });
      const t = r.data?.totals || {};
      toast({ title: 'EOSB summary emailed to Finance', description: `Sent to ${(r.data?.to || []).join(', ')} (cc ${r.data?.cc}) · ${t.count || 0} pending, balance ${aedText(t.balance)}` });
      setFinanceOpen(false);
    } catch (e) { toast({ title: 'Send failed', description: e?.response?.data?.detail || '', variant: 'destructive' }); }
    finally { setSendingFinance(false); }
  };

  const whatsappFinance = () => {
    const msg = `EOSB Summary (Stars 'N' Bars)\n`
      + `Total EOSB due: ${aedText(floats.total_due)}\n`
      + `Paid: ${aedText(floats.total_paid)} (${counts.paid || 0})\n`
      + `Unpaid balance: ${aedText(floats.total_unpaid)} (${(counts.pending || 0) + (counts.partial || 0)} outstanding)\n`
      + `In Company Vault: ${aedText(floats.total_vault)} (${counts.vault || 0})`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="space-y-6" data-testid="eosb-ledger-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="h-6 w-6 text-teal-600" /> EOSB Ledger
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">All End-of-Service Benefits across staff — paid, unpaid &amp; vault-held. Unpaid stays on top.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="eosb-export-btn"><Download className="h-4 w-4" /> Export</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportReport('pdf')} data-testid="eosb-export-pdf"><FileText className="h-4 w-4 mr-2 text-rose-600" /> PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportReport('excel')} data-testid="eosb-export-excel"><FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" className="h-9 gap-1.5 border-teal-300 text-teal-700 hover:bg-teal-50" onClick={openFinance} data-testid="eosb-email-finance-btn"><Mail className="h-4 w-4" /> Email Finance</Button>
          <Button variant="outline" size="sm" className="h-9 gap-1.5 border-green-300 text-green-700 hover:bg-green-50" onClick={whatsappFinance} data-testid="eosb-whatsapp-finance-btn"><MessageCircle className="h-4 w-4" /> WhatsApp</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={DirhamIcon} label="Total EOSB Due" value={aed(floats.total_due)} sub={`${data?.total_records || 0} record(s)`} color="bg-teal-50 border-teal-200 text-teal-800" testid="eosb-ledger-total" />
        <KpiTile icon={CheckCircle2} label="Paid" value={aed(floats.total_paid)} sub={`${counts.paid || 0} settled`} color="bg-emerald-50 border-emerald-200 text-emerald-800" testid="eosb-ledger-paid" />
        <KpiTile icon={AlertTriangle} label="Unpaid (balance)" value={aed(floats.total_unpaid)} sub={`${(counts.pending || 0) + (counts.partial || 0)} outstanding`} color="bg-rose-50 border-rose-200 text-rose-800" testid="eosb-ledger-unpaid" />
        <KpiTile icon={Landmark} label="In Company Vault" value={aed(floats.total_vault)} sub={`${counts.vault || 0} unclaimed`} color="bg-indigo-50 border-indigo-200 text-indigo-800" testid="eosb-ledger-vault" />
      </div>

      {/* View switcher */}
      <div className="inline-flex bg-gray-100 rounded-lg p-1 gap-1" data-testid="eosb-view-switch">
        <button
          onClick={() => setView('settlements')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === 'settlements' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}
          data-testid="eosb-view-settlements"
        >Settlements</button>
        <button
          onClick={() => setView('active')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${view === 'active' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}
          data-testid="eosb-view-active"
        ><Users className="h-4 w-4" /> Active Staff EOSB</button>
      </div>

      {view === 'active' && (
        <Card data-testid="eosb-active-card">
          <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-teal-600" /> Active Staff — Accrued EOSB</CardTitle>
              <p className="text-xs text-gray-500 mt-0.5">EOSB accrued as of today for {active?.count || 0} active staff.</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="rounded-lg bg-teal-50 border border-teal-200 px-3 py-1.5 text-right" data-testid="eosb-active-grandtotal">
                <p className="text-[10px] uppercase tracking-wide text-teal-600 font-medium">Grand Total EOSB</p>
                <p className="text-lg font-bold text-teal-800 leading-tight">{aed(activeTotals.eosb_total)}</p>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="eosb-active-export-btn"><Download className="h-4 w-4" /> Export</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportActive('pdf')} data-testid="eosb-active-export-pdf"><FileText className="h-4 w-4 mr-2 text-rose-600" /> PDF</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportActive('excel')} data-testid="eosb-active-export-excel"><FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <div className="relative w-full sm:w-64">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={activeSearch} onChange={(e) => setActiveSearch(e.target.value)} placeholder="Search name, code, dept…" className="pl-9 h-9" data-testid="eosb-active-search" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {activeLoading ? (
              <div className="py-12 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading active staff…</div>
            ) : activeRows.length === 0 ? (
              <div className="py-12 text-center text-gray-400 text-sm" data-testid="eosb-active-empty">No active staff found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase text-gray-400 border-b">
                      <th className="p-2.5">Employee</th>
                      <th className="p-2.5">Department</th>
                      <th className="p-2.5">Joining Date</th>
                      <th className="p-2.5 text-right">Service</th>
                      <th className="p-2.5 text-right">Basic Salary</th>
                      <th className="p-2.5 text-right">Total Salary</th>
                      <th className="p-2.5 text-right">EOSB Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRows.map((r) => (
                      <tr key={r.employee_id} className="border-b hover:bg-gray-50" data-testid={`eosb-active-row-${r.employee_code}`}>
                        <td className="p-2.5">
                          <div className="font-medium text-gray-900">{r.name}</div>
                          <div className="text-[11px] text-gray-400">{r.employee_code}</div>
                        </td>
                        <td className="p-2.5 text-gray-600">{r.department || '—'}</td>
                        <td className="p-2.5 text-gray-600">{fmtDate(r.join_date)}</td>
                        <td className="p-2.5 text-right text-gray-600">
                          {r.service_years != null ? `${r.service_years} yr` : '—'}
                          {r.under_one_year && <div className="text-[10px] text-amber-600">under 1 yr</div>}
                        </td>
                        <td className="p-2.5 text-right font-mono text-gray-700">{aed(r.basic_salary)}</td>
                        <td className="p-2.5 text-right font-mono text-gray-700">{aed(r.total_salary)}</td>
                        <td className="p-2.5 text-right font-mono font-semibold text-teal-700">{aed(r.eosb_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-teal-200 bg-teal-50/60 font-semibold" data-testid="eosb-active-footer">
                      <td className="p-2.5 text-teal-900" colSpan={2}>Grand Total ({activeRows.length})</td>
                      <td className="p-2.5"></td>
                      <td className="p-2.5"></td>
                      <td className="p-2.5 text-right font-mono text-teal-900">{aed(activeTotals.basic_salary)}</td>
                      <td className="p-2.5 text-right font-mono text-teal-900">{aed(activeTotals.total_salary)}</td>
                      <td className="p-2.5 text-right font-mono text-teal-900">{aed(activeTotals.eosb_total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {view === 'settlements' && (
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base">EOSB Records</CardTitle>
          <div className="relative w-full sm:w-72">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, code, department…" className="pl-9 h-9" data-testid="eosb-ledger-search" />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading EOSB…</div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm" data-testid="eosb-ledger-empty">No EOSB records found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase text-gray-400 border-b">
                    <th className="p-2.5">Employee</th>
                    <th className="p-2.5">Department</th>
                    <th className="p-2.5">Status</th>
                    <th className="p-2.5 text-right">Total EOSB</th>
                    <th className="p-2.5 text-right">Paid</th>
                    <th className="p-2.5 text-right">Balance</th>
                    <th className="p-2.5">Last Working Day</th>
                    <th className="p-2.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.pending;
                    const busy = busyId === r.employee_id;
                    const isVault = r.status === 'vault';
                    const isPaid = r.status === 'paid';
                    return (
                      <tr key={r.employee_id} className="border-b hover:bg-gray-50" data-testid={`eosb-ledger-row-${r.employee_code}`}>
                        <td className="p-2.5">
                          <div className="font-medium text-gray-900">{r.name}</div>
                          <div className="text-[11px] text-gray-400">{r.employee_code} · {r.employment_status}</div>
                          {r.under_one_year && <div className="text-[11px] text-amber-600">Not eligible — under 1 year</div>}
                        </td>
                        <td className="p-2.5 text-gray-600">{r.department || '—'}</td>
                        <td className="p-2.5"><Badge variant="outline" className={meta.cls} data-testid={`eosb-ledger-status-${r.employee_code}`}>{meta.label}</Badge></td>
                        <td className="p-2.5 text-right font-mono text-gray-800">{aed(r.total_amount)}</td>
                        <td className="p-2.5 text-right font-mono text-emerald-700">{aed(r.paid_amount)}</td>
                        <td className={`p-2.5 text-right font-mono font-semibold ${r.balance > 0 ? 'text-rose-700' : 'text-gray-400'}`}>{aed(r.balance)}</td>
                        <td className="p-2.5 text-gray-600">{fmtDate(r.last_working_day)}</td>
                        <td className="p-2.5 text-right">
                          {isVault ? (
                            <Button size="sm" variant="outline" className="h-8 gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-50" disabled={busy} onClick={() => releaseAndPay(r)} data-testid={`eosb-release-pay-${r.employee_code}`}>
                              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Banknote className="h-3.5 w-3.5" />} Release &amp; Pay
                            </Button>
                          ) : (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={busy} data-testid={`eosb-actions-${r.employee_code}`}>
                                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {!isPaid && (
                                  <DropdownMenuItem onClick={() => setStatus(r, 'paid')} data-testid={`eosb-mark-paid-${r.employee_code}`}><CheckCircle2 className="h-4 w-4 mr-2 text-emerald-600" /> Mark Paid</DropdownMenuItem>
                                )}
                                {!isPaid && (
                                  <DropdownMenuItem onClick={() => { setPartialAmt(''); setPartial(r); }} data-testid={`eosb-record-partial-${r.employee_code}`}><Banknote className="h-4 w-4 mr-2 text-amber-600" /> Record Partial</DropdownMenuItem>
                                )}
                                {!isPaid && r.balance > 0 && <DropdownMenuSeparator />}
                                {!isPaid && r.balance > 0 && (
                                  <DropdownMenuItem onClick={() => moveToVault(r)} data-testid={`eosb-move-vault-${r.employee_code}`}><Landmark className="h-4 w-4 mr-2 text-indigo-600" /> Move to Vault</DropdownMenuItem>
                                )}
                                {isPaid && (
                                  <DropdownMenuItem onClick={() => setStatus(r, 'pending')} data-testid={`eosb-mark-unpaid-${r.employee_code}`}><AlertTriangle className="h-4 w-4 mr-2 text-rose-600" /> Revert to Unpaid</DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Record partial payment */}
      <Dialog open={!!partial} onOpenChange={(o) => !o && setPartial(null)}>
        <DialogContent className="max-w-sm" data-testid="eosb-partial-dialog">
          <DialogHeader><DialogTitle>Record Partial Payment</DialogTitle></DialogHeader>
          {partial && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">{partial.name} · balance <b>{aed(partial.balance)}</b></p>
              <div>
                <Label className="text-xs">Payment amount (AED)</Label>
                <Input type="number" value={partialAmt} onChange={(e) => setPartialAmt(e.target.value)} placeholder="e.g. 5000" data-testid="eosb-partial-amount" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPartial(null)}>Cancel</Button>
            <Button onClick={submitPartial} data-testid="eosb-partial-submit">Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email to Finance */}
      <Dialog open={financeOpen} onOpenChange={setFinanceOpen}>
        <DialogContent className="max-w-md" data-testid="eosb-finance-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-teal-600" /> Email Pending EOSB to Finance</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Sends the pending-EOSB summary (PDF + outstanding totals) to the recipients below. HR is auto-CC'd.</p>
            <div className="flex gap-2">
              <Input value={newFinanceEmail} onChange={(e) => setNewFinanceEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFinanceEmail())} placeholder="finance@company.com" data-testid="eosb-finance-email-input" />
              <Button variant="outline" onClick={addFinanceEmail} data-testid="eosb-finance-add"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {financeEmails.length === 0 ? <span className="text-xs text-gray-400">No recipients yet</span> :
                financeEmails.map((e) => (
                  <Badge key={e} variant="secondary" className="gap-1">{e}<button onClick={() => removeFinanceEmail(e)} className="ml-1"><X className="h-3 w-3" /></button></Badge>
                ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFinanceOpen(false)}>Cancel</Button>
            <Button onClick={sendFinance} disabled={sendingFinance} className="gap-1.5" data-testid="eosb-finance-send">
              {sendingFinance ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EOSBLedger;
